import { __assign, __makeTemplateObject, __read, __rest, __values } from "tslib";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useCallback, } from 'react';
import { styled, useEventCallback, useForkRef, useKeyboardMoveFocus, useOnlyOneFocusable, useThemeProps, } from '../../../foundation';
import { RcIconButtonClasses } from '../../Buttons/IconButton/utils';
import { RcVisuallyHidden } from '../../VisuallyHidden';
import { RcDialPadButton } from '../DialPadButton';
import { useRcDialerContext } from '../utils';
import { DialPadStyle } from './styles';
import { ACCEPTABLE_KEYS, DIALER_PAD_ICONS, DIALER_PAD_PLUS, useKeyAudio, } from './utils';
var _RcDialPad = forwardRef(function (inProps, ref) {
    var props = useThemeProps({ props: inProps, name: 'RcDialPad' });
    var sounds = props.sounds, volume = props.volume, autoSize = props.autoSize, muted = props.muted, classes = props.classes, onChangeProp = props.onChange, longPressDelay = props.longPressDelay, action = props.action, persistBgTime = props.persistBgTime, control = props.control, getDialPadButtonProps = props.getDialPadButtonProps, _a = props.externalWindow, externalWindow = _a === void 0 ? window : _a, sinkId = props.sinkId, divProps = __rest(props, ["sounds", "volume", "autoSize", "muted", "classes", "onChange", "longPressDelay", "action", "persistBgTime", "control", "getDialPadButtonProps", "externalWindow", "sinkId"]);
    var containerRef = useRef(null);
    var hiddenRef = useRef(null);
    var keyTimerMapRef = useRef({});
    var forkRef = useForkRef(containerRef, ref);
    var _b = useRcDialerContext(), onInsertRef = _b.onInsertRef, dialPadActionRef = _b.dialPadActionRef;
    var focusedIndexRef = useRef(0);
    var _c = useOnlyOneFocusable({
        focusedIndexRef: focusedIndexRef,
        containerRef: containerRef,
    }), focusIndex = _c.focusIndex, getItemProps = _c.getItemProps, moveFocusedId = _c.moveFocusedId;
    var dialButtonKey = moveFocusedId + "-dial-button";
    var onKeyFocusedIndexHandle = useKeyboardMoveFocus({
        options: DIALER_PAD_ICONS,
        focusedIndexRef: focusedIndexRef,
        infinite: true,
        columns: 3,
        onFocusedIndexChange: function (event, toIndex) {
            focusedIndexRef.current = toIndex;
            focusIndex(toIndex);
            event.preventDefault();
        },
    }).onKeyFocusedIndexHandle;
    var audioProcessor = useCallback(function (audio) {
        if (typeof volume !== 'undefined')
            audio.volume = volume;
        if (typeof muted !== 'undefined')
            audio.muted = muted;
        if (typeof audio['setSinkId'] === 'function')
            audio['setSinkId'](
            // when pass undefined, use '' to remove sinkId to default
            sinkId || '');
    }, [muted, sinkId, volume]);
    var play = useKeyAudio({
        sounds: sounds,
        processor: audioProcessor,
    });
    var playAudio = useEventCallback(function (value) {
        var _a;
        var key = value === DIALER_PAD_PLUS ? '0' : value;
        var keyButtonElm = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[" + dialButtonKey + "=\"" + key + "\"]");
        if (keyButtonElm) {
            // * to keep color when quick trigger insert key or click very quick
            var oldTimer = keyTimerMapRef.current[key];
            if (oldTimer)
                clearTimeout(oldTimer);
            keyButtonElm.classList.add(RcIconButtonClasses.persistBg);
            keyTimerMapRef.current[key] = window.setTimeout(function () {
                keyButtonElm.classList.remove(RcIconButtonClasses.persistBg);
                delete keyTimerMapRef.current[key];
            }, persistBgTime);
        }
        return play(value);
    });
    var handleChange = function (value, reason) {
        var _a;
        playAudio(value);
        (_a = onInsertRef === null || onInsertRef === void 0 ? void 0 : onInsertRef.current) === null || _a === void 0 ? void 0 : _a.call(onInsertRef, value, reason);
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(value, reason);
    };
    var handleKeyboardEffect = useEventCallback(function (value, isKeyup) {
        var _a, _b;
        if (isKeyup) {
            handleChange(value, 'customKeyboard');
            var toIndex = ACCEPTABLE_KEYS.findIndex(function (x) { return x === value; });
            focusedIndexRef.current = toIndex;
            // * mean that current focus on that dialPad, move focus to dialPad container
            if (((_a = externalWindow.document.activeElement) === null || _a === void 0 ? void 0 : _a.getAttribute(dialButtonKey)) !== null) {
                (_b = hiddenRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }
        }
    });
    var handleKeyEffect = useEventCallback(function (value, reason) {
        handleChange(value, reason);
    });
    var forkActionRef = useForkRef(dialPadActionRef, action);
    useImperativeHandle(forkActionRef, function () { return ({ playAudio: playAudio }); }, [playAudio]);
    useImperativeHandle(control, function () { return ({ handleKeyboardEffect: handleKeyboardEffect }); }, [
        handleKeyboardEffect,
    ]);
    // * clean when unmount
    useEffect(function () { return function () {
        var e_1, _a;
        try {
            for (var _b = __values(Object.values(keyTimerMapRef.current)), _c = _b.next(); !_c.done; _c = _b.next()) {
                var timer = _c.value;
                clearTimeout(timer);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }; }, []);
    return (React.createElement("div", __assign({ ref: forkRef }, divProps),
        React.createElement(RcVisuallyHidden, { ref: hiddenRef, tabIndex: -1 }),
        DIALER_PAD_ICONS.map(function (_a, index) {
            var _b;
            var _c = __read(_a, 3), symbol = _c[0], key = _c[1], additionLabel = _c[2];
            var ariaLabelArr = additionLabel
                ? [key, additionLabel]
                : [key];
            return (React.createElement(RcDialPadButton, __assign({ classes: classes, symbol: symbol, key: key, value: key, onKeyEffect: handleKeyEffect, externalWindow: externalWindow }, getItemProps(index), { "aria-label": ariaLabelArr.join(','), onKeyDown: onKeyFocusedIndexHandle }, (_b = {}, _b[dialButtonKey] = key, _b), getDialPadButtonProps === null || getDialPadButtonProps === void 0 ? void 0 : getDialPadButtonProps(key), (key === '0'
                ? {
                    longPressValue: DIALER_PAD_PLUS,
                    longPressDelay: longPressDelay,
                }
                : {}))));
        })));
});
/** @release */
var RcDialPad = styled(_RcDialPad)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), DialPadStyle);
RcDialPad.defaultProps = {
    persistBgTime: 200,
    volume: 1,
    muted: false,
    longPressDelay: 1000,
};
RcDialPad.displayName = 'RcDialPad';
export { RcDialPad };
var templateObject_1;
